import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DateTime, FixedOffsetZone } from 'luxon';
import { JackingFieldTemplateEditor, JackingFormInstanceEditor } from '../../../../../../../models/jacking-v2/jacking.models';
import { PtLuxonDate } from '../../../../../../../models/projects/date.models';
import { DateService } from '../../../../../../../services/date/date.service';

@Component({
  selector: 'jacking-field-date-time',
  templateUrl: 'jacking-field-date-time.component.html'
})
export class JackingFieldDateTimeComponent implements OnInit, OnChanges {
  @Input() field: JackingFieldTemplateEditor;
  @Input() editor: JackingFormInstanceEditor;
  @Input() confirmed: boolean | null;
  @Input() activitySpansMultipleDays: boolean = false;

  // Stored value in UTC + stored offset HOURS
  @Input() valueUtc: PtLuxonDate | null;
  @Input() offsetHours: number | null;
  @Input() isReadOnly: boolean = false;

  @Output() valueChange = new EventEmitter<{
    valueUtc: PtLuxonDate | null;
    offsetHours: number | null;
    confirmed: boolean | null;
    dateChanged?: boolean;
  }>();

  timeToShow = '';
  selectedLocalDateIso = '';
  confirmedLocal = false;

  // text-mask pattern: HH:MM, with hour second digit adjusted dynamically
  mask: any[] = [new RegExp('[0-2]'), new RegExp('\\d'), ':', new RegExp('[0-5]'), new RegExp('\\d')];

  // Local value used by the UI controls (date picker + offset picker)
  localPt: PtLuxonDate = new PtLuxonDate();

  constructor(private dateService: DateService) {}

  ngOnInit() {
    this.hydrateFromInputs();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Always keep confirm UI in sync, but do not rehydrate the time/date because of it
    if (changes['confirmed']) {
      this.confirmedLocal = !!this.confirmed;
    }

    // Only rehydrate if the actual datetime inputs changed
    if (changes['valueUtc'] || changes['offsetHours'] || changes['editor']) {
      this.hydrateFromInputs();
    }
  }

  private hydrateFromInputs() {
    // Confirmed UI state
    this.confirmedLocal = !!this.confirmed;

    const dtUtc = this.tryReadUtcDateTime(this.valueUtc);

    if (!dtUtc) {
      this.localPt.setDate(null);
      this.timeToShow = '';
      this.selectedLocalDateIso = '';
      this.updateMask();
      return;
    }

    const pt = new PtLuxonDate();
    pt.dateTime = dtUtc;

    // Apply stored offset hours to get the local view (Luxon stores offset in minutes internally)
    const offsetH = this.offsetHours ?? null;
    if (offsetH != null) {
      pt.setOffset(offsetH * 3600, false); // seconds, keep local time
    }

    this.localPt = pt;

    this.timeToShow = this.localPt?.dateTime ? this.localPt.dateTime.toFormat('HH:mm') : '';
    this.selectedLocalDateIso = this.localPt?.dateTime ? this.localPt.dateTime.toISODate() : '';

    this.updateMask();
  }

  private tryReadUtcDateTime(incoming: any): DateTime | null {
    if (!incoming) {
      return null;
    }

    // Supports PtLuxonDate, { dateTime: "..." }, or raw ISO strings
    const raw = incoming?.dateTime ?? incoming;

    let dt: DateTime | null = null;

    if (raw && typeof raw === 'object' && typeof raw.dateTime === 'string') {
      dt = DateTime.fromISO(raw.dateTime, { zone: 'utc' });
    } else if (typeof raw === 'string') {
      dt = DateTime.fromISO(raw, { zone: 'utc' });
    } else if (raw && typeof raw === 'object' && typeof raw.toISO === 'function') {
      dt = raw as DateTime;
    }

    if (!dt || !dt.isValid) {
      return null;
    }

    return dt.toUTC();
  }

  async onTimeTextChanged(timeToShow: string) {
    this.timeToShow = (timeToShow || '').trim();
    this.updateMask();
    await this.onTimeAltered(false);
  }

  async onDateChanged(dt: DateTime) {
    this.selectedLocalDateIso = dt?.toISODate?.() || '';
    await this.onTimeAltered(true);
  }

  onOffsetSelected(ptLocal: PtLuxonDate) {
    if (this.field?.readOnly) {
      return;
    }
    if (!ptLocal?.dateTime) {
      return;
    }

    this.localPt = ptLocal;

    // Keep displayed fields in sync
    this.timeToShow = ptLocal.dateTime.toFormat('HH:mm');
    this.selectedLocalDateIso = ptLocal.dateTime.toISODate();
    this.updateMask();

    // If a change requires confirmation, reset it
    if (this.field?.requiresConfirmation) {
      this.confirmedLocal = false;
    }

    const stored = this.toStored(this.localPt);
    this.valueChange.emit({ ...stored, confirmed: this.confirmedLocal, dateChanged: false });
  }

  onConfirmedChanged(confirmed: boolean) {
    if (this.field?.readOnly) {
      return;
    }

    this.confirmedLocal = confirmed;

    const stored = this.localPt?.dateTime ? this.toStored(this.localPt) : { valueUtc: null, offsetHours: null };
    this.valueChange.emit({ ...stored, confirmed: this.confirmedLocal, dateChanged: false });
  }

  private async onTimeAltered(dateChanged: boolean) {
    if (this.field?.readOnly) {
      return;
    }

    const timeToShow = (this.timeToShow || '').trim();

    // check date is fully entered - e.g. not half typed 10:2_ etc
    const allowed = await this.dateService.timeAllowed(this.timeToShow, this.localPt.dateTime);
    if (!allowed) {
      return;
    }

    // Clear
    if (!timeToShow) {
      this.localPt.setDate(null);

      const confirmed = this.field?.requiresConfirmation ? false : this.confirmedLocal;
      this.confirmedLocal = confirmed;

      this.valueChange.emit({ valueUtc: null, offsetHours: null, confirmed, dateChanged });
      return;
    }

    const baseLocalMidnight = this.getBaseLocalMidnight();
    const ptLocal = await this.dateService.setPtLuxonDateFromUserEnteredString(timeToShow, baseLocalMidnight);

    if (!ptLocal?.dateTime || !ptLocal.dateTime.isValid) {
      return;
    }

    this.localPt = ptLocal;

    // Keep displayed fields in sync
    this.timeToShow = ptLocal.dateTime.toFormat('HH:mm');
    this.selectedLocalDateIso = ptLocal.dateTime.toISODate();
    this.updateMask();

    const stored = this.toStored(ptLocal);
    this.valueChange.emit({ ...stored, confirmed: this.confirmedLocal, dateChanged });
  }

  private getBaseLocalMidnight(): DateTime {
    const startUtc = this.editor?.startDate?.dateTime?.toUTC?.() ? this.editor.startDate.dateTime.toUTC() : DateTime.utc();

    // Choose which day the user is editing (either explicit selected date or start local date)
    const startOffsetH = this.editor?.startOffset ?? 0;
    const startLocalIso = startUtc.plus({ hours: startOffsetH }).toISODate();

    const dayIso = this.activitySpansMultipleDays
      ? (this.selectedLocalDateIso || startLocalIso)
      : startLocalIso;

    // Create midnight in UTC for that ISO date, then apply the chosen offset (keep local time)
    const baseUtcMidnight = DateTime.fromISO(dayIso, { zone: 'utc' }).startOf('day');

    const pt = new PtLuxonDate();
    pt.dateTime = baseUtcMidnight;

    // Use current stored hours if provided, else fall back to editor start offset
    const offsetH = this.offsetHours != null ? this.offsetHours : startOffsetH;
    pt.setOffset(offsetH * 3600, true);

    return pt.dateTime;
  }

  // Exposes the effective minimum date bound for the field date picker.
  get pickerMinDate(): DateTime | null {
    const legsFreeBounds = this.getLegsFreePickerBounds();
    if (legsFreeBounds) {
      return legsFreeBounds.minDateTime;
    }

    return this.rebuildLocalBound(this.editor?.startDate, this.editor?.startOffset ?? null);
  }

  // Exposes the effective maximum date bound for the field date picker.
  get pickerMaxDate(): DateTime | null {
    const legsFreeBounds = this.getLegsFreePickerBounds();
    if (legsFreeBounds) {
      return legsFreeBounds.maxDateTime;
    }

    return this.rebuildLocalBound(this.editor?.endDate, this.editor?.endOffset ?? this.editor?.startOffset ?? null);
  }

  // Builds form-wide date bounds for the Legs Free picker so reloads keep the valid range.
  private getLegsFreePickerBounds(): { minDateTime: DateTime; maxDateTime: DateTime } | null {
    const isLegsFreeTime = (this.field?.label || '').trim().toLowerCase() === 'legs free time';
    if (!isLegsFreeTime) {
      return null;
    }

    const candidates: DateTime[] = [];

    const pushCandidate = (value: DateTime | null | undefined) => {
      if (value?.isValid) {
        candidates.push(value);
      }
    };

    pushCandidate(this.rebuildLocalBound(this.editor?.startDate, this.editor?.startOffset ?? null));
    pushCandidate(this.rebuildLocalBound(this.editor?.endDate, this.editor?.endOffset ?? this.editor?.startOffset ?? null));

    for (const row of this.editor?.jackingEvents?.eventRows || []) {
      pushCandidate(row?.eventDate?.dateTime || null);
    }

    const finalValues: any = this.editor?.jackingFinalValues;
    const finalBlocks = [
      finalValues?.finalLegValues,
      finalValues?.finalPenetrations,
      finalValues?.differenceFromExpected,
      finalValues?.finalRpd,
      finalValues?.finalAirGap
    ];

    for (const block of finalBlocks) {
      const dt = block?.timeUtc?.dateTime ?? block?.timeUtc;
      pushCandidate(dt || null);
    }

    if (!candidates.length) {
      return null;
    }

    return candidates.reduce((bounds, current) => ({
      minDateTime: current < bounds.minDateTime ? current : bounds.minDateTime,
      maxDateTime: current > bounds.maxDateTime ? current : bounds.maxDateTime
    }), {
      minDateTime: candidates[0],
      maxDateTime: candidates[0]
    });
  }

  // Reconstructs a local DateTime for picker bounds from a stored UTC value plus offset.
  private rebuildLocalBound(valueUtc: PtLuxonDate | null | undefined, offsetHours: number | null): DateTime | null {
    const dtUtc = this.tryReadUtcDateTime(valueUtc);
    if (!dtUtc) {
      return null;
    }

    if (offsetHours == null) {
      return dtUtc;
    }

    return dtUtc.setZone(FixedOffsetZone.instance(offsetHours * 60));
  }

  private toStored(ptLocal: PtLuxonDate) {
    if (!ptLocal?.dateTime) {
      return { valueUtc: null as PtLuxonDate | null, offsetHours: null as number | null };
    }

    const utc = new PtLuxonDate();
    utc.dateTime = ptLocal.dateTime.toUTC();

    return {
      valueUtc: utc,
      offsetHours: ptLocal.dateTime.offset / 60
    };
  }

  private updateMask() {
    // If first hour digit is 2, second digit must be 0-3, else 0-9
    const first = (this.timeToShow || '')[0];

    const secondHourDigit = first === '2'
      ? new RegExp('[0-3]')
      : new RegExp('\\d');

    this.mask = [new RegExp('[0-2]'), secondHourDigit, ':', new RegExp('[0-5]'), new RegExp('\\d')];
  }
}



// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v2/components/jacking-field-component/sub-components/jacking-field-date-time/jacking-field-date-time.component.ts